import { ProductService } from 'hb4e-ui-product'
import { List, ListType } from 'hb4e-ui-context'

const {
  getProductAcademicPrice,
  getProductAuthors,
  getProductAvailabilityId,
  getProductDiscipline,
  getProductExpirationDate,
  getProductImage,
  getProductLanguage,
  getProductLength,
  getProductLengthLabel,
  getProductPublicationDate,
  getProductRevisionDate,
  getProductSignpost,
  getProductTitle,
  getProductTypeName,
  isItemExpired,
  hasEducatorCopy,
  hasSupplement,
  hasTeachingNote,
  getValidSupplements,
  hasEducatorSupplements,
  hasStudentSupplements,
  hasSupplements,
  shouldHideProductAddToCart,
  getProductLink,
  getEPUBProductLink,
  shouldHideAddToCoursepack,
} = ProductService

/**
 * Calculates the total numbers of lists.
 *
 * Special list types like saved items might not be included in the
 * list if a user hasn't used them yet. So if they are not present,
 * we need to add them to the count since the UI will display them
 * regardless.
 *
 * @param {Array<List>} lists - The user's lists, used to determine which
 *    static list types the server knows about
 * @param {number} totalListCount - the total number of lists the user has created
 *
 * @return {number} The total number of lists a user has
 */

export const getListsCount = (
  lists = [],
  totalListCount = 0,
  shouldExcludeFreeTrials = false,
  shouldExcludePurchasedItems = false,
) =>
  totalListCount +
  List.getStaticLists().filter((list, i) => {
    switch (list.getType()) {
      case ListType.FREE_TRIALS:
        return !shouldExcludeFreeTrials
      case ListType.PURCHASED_ITEMS:
        return !shouldExcludePurchasedItems
      default:
        return true
    }
  }).length -
  lists.filter((list) => list.type !== ListType.PERSONAL).length

/**
 * If the item has an expireDate property, return true, otherwise return false.
 * @param item - The item to check
 */
export const hasExpirationDate = (item) => !!item?.expireDate

/**
 * Gets the product details from a list item necessary for displaying
 * the list item.
 *
 * @param item - a list item, such as one returned from List#getItems()
 *
 * @returns An object with the item's product details
 */
export const getProductDetailsForListItem = (item) => {
  return {
    expirationDate: getProductExpirationDate(item),
    isExpired: isItemExpired(item),
    productAuthors: getProductAuthors(item.product),
    productAvailabilityID: getProductAvailabilityId(item.product),
    productDiscipline: getProductDiscipline(item.product),
    productImage: getProductImage(item.product),
    productLanguage: getProductLanguage(item.product),
    productLength: getProductLength(item.product),
    productLengthLabel: getProductLengthLabel(item.product),
    productPrice: getProductAcademicPrice(item.product),
    productPublicationDate: getProductPublicationDate(item.product),
    productRevisionDate: getProductRevisionDate(item.product),
    productTitle: getProductTitle(item.product),
    productType: getProductTypeName(item.product),
    signpost: getProductSignpost(item.product),
    hasEducatorCopy: hasEducatorCopy(item.product),
    hasSupplement: hasSupplement(item.product),
    hasTeachingNote: hasTeachingNote(item.product),
    supplements: getValidSupplements(item.product),
    hasEducatorSupplements: hasEducatorSupplements(item.product),
    hasStudentSupplements: hasStudentSupplements(item.product),
    hasSupplements: hasSupplements(item.product),
    shouldHideProductAddToCart: shouldHideProductAddToCart(item.product),
    epubProductLink: getEPUBProductLink(item),
    productLink: getProductLink(item),
    shouldHideAddToCoursepack: shouldHideAddToCoursepack(item.product),
  }
}
